import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {CacheService} from '../services/cache.service';
import {MatSort, SortDirection} from '@angular/material/sort';
import {AfterViewInit, Directive, HostListener, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {merge, Subscription} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {MatPaginator} from '@angular/material/paginator';
import {BaseNumberApi, BaseNumberDatasource, BaseNumberModel, ListFilter, Pagination} from "@shared/base";

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class BaseNumberList<M extends BaseNumberModel> implements AfterViewInit, OnInit, OnDestroy {
  dataSource: BaseNumberDatasource<M>;
  filterForm: UntypedFormGroup;
  protected subscriptions = new Subscription();

  selected: M;

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  protected formBuilder = inject(UntypedFormBuilder);
  private cacheService = inject(CacheService);

  protected abstract listApi: BaseNumberApi<M>;
  protected abstract listCachePrefix: string;

  ngOnInit() {
    this.dataSource = new BaseNumberDatasource<M>(this.listApi);
    if (!this.filterForm) {
      this.filterForm = this.cacheService.get(`${this.listCachePrefix}-list-filter`, this.defaultListForm()).item as UntypedFormGroup;
    }

    const pagination: Pagination = this.cacheService.get(`${this.listCachePrefix}-list-pagination`, this.defaultPagination()).item as Pagination;

    this.paginator.pageIndex = pagination.page - 1;
    this.paginator.pageSize = pagination.limit;
    this.sort.active = pagination.orderBy;
    this.sort.direction = <SortDirection>pagination.direction;

    this.loadData();
  }

  abstract defaultListForm(): UntypedFormGroup;

  abstract defaultPagination(): Pagination;

  abstract prepareFilter(values: Record<string, unknown>): Record<string, unknown>;

  ngAfterViewInit() {
    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page).pipe(
        tap(() => this.loadData())
      ).subscribe()
    );

    this.subscriptions.add(
      this.dataSource.subject$.pipe(
        filter(value => value && value.length > 0),
      ).subscribe(invoices => {
        if (!this.selected && invoices.length > 0) {
          this.onSelect(invoices[0].number);
        }
      })
    );
  }

  loadData() {
    const pagination: Pagination = this.cacheService.get(`${this.listCachePrefix}-list-pagination`, new Pagination()).item as Pagination;
    pagination.page = this.paginator.pageIndex + 1;
    pagination.limit = this.paginator.pageSize;
    pagination.orderBy = this.sort.active;
    pagination.direction = this.sort.direction;

    const filterValues = this.prepareFilter({...this.filterForm.value});

    this.dataSource.load(filterValues as ListFilter, pagination);
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    let move = 0;
    if (event.key === 'ArrowDown') {
      move = 1;
    } else if (event.key === 'ArrowUp') {
      move = -1;
    }

    if (move !== 0) {
      const currentIndex = this.dataSource.data.findIndex(value => value.number === this.selected.number);
      const index = currentIndex + move;

      if (index < 0) {
        if (this.paginator.hasPreviousPage()) {
          this.selected = undefined;
          this.paginator.previousPage();
        }
        return;
      }

      if (index > this.dataSource.data.length - 1) {
        if (this.paginator.hasNextPage()) {
          this.selected = undefined;
          this.paginator.nextPage();
        }
        return;
      }

      if (currentIndex !== index) {
        this.onSelect(this.dataSource.data[index].number);
      }
    }
  }

  onSelect(number: number) {
    this.listApi.findByNumber(number).subscribe(value => this.selected = value);
  }

  search() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
